// baba Email — the web client for the gateway's /email/* routes. The contract
// is docs/email/CLIENT_CONTRACT.md in the baba-v2 repo; every shape below is
// copied from it. Auth rides on gatewayFetch (the baba_web JWT), so nothing
// here touches cookies or tokens directly.
//
// The feature is gated per account: `GET /email/usage` says `enabled`, and
// every other route answers 503 `email_disabled` for an account that is not.
// That code is never an error to show — it means "hide the surface", and the
// gate provider (components/email/EmailGate.tsx) listens for it through
// `onEmailDisabled` so one stray 503 anywhere collapses the whole surface.
import { GatewayError, gatewayFetch, gatewayFetchJson } from './gateway'

export type EmailStatus = 'received' | 'identified' | 'translated' | 'ready' | 'held' | 'failed'

export type EmailKind = 'bill' | 'order' | 'notice' | 'letter' | 'receipt' | 'appointment' | 'promotion' | 'other'

export interface EmailUsage {
  enabled: boolean
  used: number
  limit: number
  tier: string
  resetsAt: string | null
  /** The address to show everywhere (t@itsbaba.com). Never hard-coded. */
  forwardTo: string
}

export interface InboxItem {
  id: string
  status: EmailStatus
  receivedAt: string
  originalSender: string | null
  originalSenderName: string | null
  originalSubject: string | null
  originalDate: string | null
  title: string | null
  kind: EmailKind | string | null
  preview: string | null
  attachmentCount: number
  read: boolean
  updatedAt?: string
}

export interface InboxResponse {
  items: InboxItem[]
  forwardTo: string
}

export type Inline =
  | { t: 'text'; id: string; text: string; bold?: boolean }
  | { t: 'link'; id: string; text: string; href: string }
  | { t: 'br' }

export type Block =
  | { type: 'heading'; level: 1 | 2 | 3; inlines: Inline[] }
  | { type: 'paragraph'; inlines: Inline[] }
  | { type: 'list'; ordered: boolean; items: Inline[][] }
  | { type: 'table'; rows: Inline[][][] }
  | { type: 'button'; id: string; text: string; href: string }
  | { type: 'image'; alt: string; src: string }
  | { type: 'rule' }

export interface EmailBrief {
  title: string
  kind: EmailKind | string
  points: string[]
  senderName: string
}

export interface EmailAttachment {
  index: number
  name: string
  contentType: string
  size: number
  status: string
  translation: string | null
  stored: boolean
}

export interface EmailMessage {
  id: string
  status: EmailStatus
  receivedAt: string
  originalSender: string | null
  originalSenderName: string | null
  originalSubject: string | null
  originalDate: string | null
  language: string | null
  brief: EmailBrief | null
  sourceBlocks: Block[]
  translatedBlocks: Block[] | null
  attachments: EmailAttachment[]
  read: boolean
  replySent: boolean
  error: string | null
  timings: Record<string, unknown> | null
}

export type AddressSource = 'account' | 'claim' | 'code'

export interface EmailAddress {
  id: string
  address: string
  source: AddressSource
  verified: boolean
  createdAt: string
}

export interface AddressesResponse {
  addresses: EmailAddress[]
  forwardTo: string
}

export interface ClaimResponse {
  claimed: boolean
  address: string | null
  moved: number
}

export const EMAIL_DISABLED_CODE = 'email_disabled'
export const BAD_CODE = 'bad_code'
export const BAD_TOKEN = 'bad_token'

export function isEmailDisabledError(e: unknown): boolean {
  return e instanceof GatewayError && e.status === 503 && e.code === EMAIL_DISABLED_CODE
}

export function gatewayErrorCode(e: unknown): string | null {
  return e instanceof GatewayError ? e.code : null
}

type DisabledListener = () => void
const disabledListeners = new Set<DisabledListener>()

/** Subscribe to "the server said email_disabled". Returns the unsubscribe. */
export function onEmailDisabled(listener: DisabledListener): () => void {
  disabledListeners.add(listener)
  return () => {
    disabledListeners.delete(listener)
  }
}

function emitDisabled(): void {
  for (const listener of disabledListeners) listener()
}

async function emailJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await gatewayFetchJson<T>(path, init)
  } catch (e) {
    if (isEmailDisabledError(e)) emitDisabled()
    throw e
  }
}

export function fetchEmailUsage(): Promise<EmailUsage> {
  return emailJson<EmailUsage>('/email/usage')
}

export function fetchInbox(since?: string | null): Promise<InboxResponse> {
  const query = since ? `?since=${encodeURIComponent(since)}` : ''
  return emailJson<InboxResponse>(`/email${query}`)
}

export function fetchEmail(id: string): Promise<EmailMessage> {
  return emailJson<EmailMessage>(`/email/${encodeURIComponent(id)}`)
}

export function markEmailRead(id: string): Promise<unknown> {
  return emailJson(`/email/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ read: true }) })
}

export function archiveEmail(id: string): Promise<unknown> {
  return emailJson(`/email/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ archived: true }) })
}

export function retryEmail(id: string): Promise<{ status: EmailStatus }> {
  return emailJson<{ status: EmailStatus }>(`/email/${encodeURIComponent(id)}/retry`, { method: 'POST' })
}

export function fetchAddresses(): Promise<AddressesResponse> {
  return emailJson<AddressesResponse>('/email/addresses')
}

export function addAddress(address: string): Promise<{ sent: true }> {
  return emailJson<{ sent: true }>('/email/addresses', { method: 'POST', body: JSON.stringify({ address }) })
}

export function verifyAddress(address: string, code: string): Promise<{ verified: true }> {
  return emailJson<{ verified: true }>('/email/addresses/verify', { method: 'POST', body: JSON.stringify({ address, code }) })
}

export function removeAddress(id: string): Promise<unknown> {
  return emailJson(`/email/addresses/${encodeURIComponent(id)}`, { method: 'DELETE' })
}

export function claimEmailAddress(token: string): Promise<ClaimResponse> {
  return emailJson<ClaimResponse>('/email/claim', { method: 'POST', body: JSON.stringify({ token }) })
}

export interface AttachmentBlob {
  /**
   * The file, as bytes. Deliberately NOT handed back as a blob: URL: a URL
   * made from this blob would carry the SENDER's content type, and a
   * `text/html` part opened from it runs as this origin. The page wraps it in
   * a type it has vetted before it shows or saves anything.
   */
  blob: Blob
  /** The reported type, lowercased, without parameters (`text/plain`). */
  contentType: string
  /** The `charset` parameter, when the server sent one (text parts). */
  charset: string | null
}

/**
 * The attachment route streams the original file behind the bearer, so a
 * plain <a href> to it would 401. Download through the API client and hand
 * back the bytes and their reported type.
 */
export async function fetchAttachmentBlob(id: string, index: number): Promise<AttachmentBlob> {
  const response = await gatewayFetch(`/email/attachments/${encodeURIComponent(id)}/${index}`)
  if (!response.ok) {
    let code: string | null = null
    try {
      const body = (await response.json()) as { error?: { code?: string | null } }
      code = body?.error?.code ?? null
    } catch {}
    const error = new GatewayError(`Attachment download failed (${response.status})`, response.status, code)
    if (isEmailDisabledError(error)) emitDisabled()
    throw error
  }
  const blob = await response.blob()
  const reported = response.headers.get('content-type') || blob.type || 'application/octet-stream'
  const [type, ...params] = reported.split(';')
  const charset = params
    .map((param) => param.trim().split('='))
    .find(([key]) => key?.trim().toLowerCase() === 'charset')?.[1]
  return {
    blob,
    contentType: type.trim().toLowerCase(),
    charset: charset ? charset.trim().replace(/^"|"$/g, '') : null,
  }
}

/** A `mailto:` that opens the mail app addressed to the forward address, no subject. */
export function forwardMailto(forwardTo: string): string {
  return `mailto:${forwardTo}`
}
